import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Stack } from 'react-bootstrap';
import { BrowserView, MobileView } from 'react-device-detect';
import { useQueryClient } from '@tanstack/react-query';
import JobListManagerHeaderMobile from '@company/components/JobList/JobListManagerHeader/JobListManagerHeaderMobile';
import JobListManagerHeader from '@company/components/JobList/JobListManagerHeader/JobListManagerHeader';
import ModalJobMembers from '@company/components/JobList/JobTable/JobTableItemDesktop/ModalJobMembers';
import ModalShareJobLink from '@company/pages/JobOverview/Detail/ModalShareJobLink';
import JobShiftsView from '@company/components/JobList/JobShiftsView';
import { useJobListQueries } from '@company/hooks/useJobListQueries';
import JobListView from '@company/components/JobList/JobListView';
import { getJobShareLinkInfo } from '@company/services/job/api';
import {
  approveTimeSheet,
  onApproveTimeSheetSuccess,
  onCancelShiftSuccess,
  onMarkPaidSuccess,
  onSubmitHoursSuccess,
} from '@utils/JobActivityUtils';
import {
  useHideJobMutation,
  useMissingActivitiesNoticedMutation,
  useMissingHoursNoticedMutation,
} from '@company/hooks/useJobListMutation';
import { ModalConfirm, ThError, ThLoading } from '@components/elements';
import { ActivityDetail } from '@th-types/daily-activity.type';
import IModalConfirmModel from '@th-types/confirm-model.type';
import IJobDetail, {
  JobAssignment,
  JobMember,
  JobShareInfo,
} from '@th-types/job.detail.type';
import {
  JobListViews,
  IJobSearchType,
  IJobListFilters,
  IJobSearchScope,
  IJobSearchPeriod,
  JobListFiltersKeys,
  NewApplicantsCounterResults,
} from '@th-types/jobs.type';
import { updateJobListSearchParams } from '@utils/JobsSearchParamsUtils';
import { isShiftPayable } from '@utils/ShiftsUtils';
import {
  completeJobAssignmentOnboardingUtil,
  markShiftAsPaid,
} from '@utils/JobAssignmentUtils';
import {
  getMarkPaidModalConfirmData,
  markPaidRefreshCounterQueryData,
  submitHoursRefreshCounterQueryData,
} from '@utils/JobUtils';
import useAlert from '@hooks/useAlert';
import { useUserContext } from '@state/userContext';

export default function JobList() {
  const { showSuccessAlert, showWarningAlert, alertMessage } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showModalJobMembers, setShowModalJobMembers] = useState(false);
  const [jobMembers, setJobMembers] = useState<JobMember[]>([]);
  const [modalConfirmModel, setModalConfirmModel] =
    useState<IModalConfirmModel>();
  const [showShareJobLinkModal, setShowShareJobLinkModal] = useState(false);

  const JOB_VIEW_TYPE = 'jobViewType';

  const [searchParams, setSearchParams] = useSearchParams();
  const searchFilter = searchParams.get(JobListFiltersKeys.SEARCH_TEXT);
  const startDate = searchParams.get(JobListFiltersKeys.DATE_FROM);
  const endDate = searchParams.get(JobListFiltersKeys.DATE_TO);
  const isActive = searchParams.get(JobListFiltersKeys.IS_ACTIVE);
  const scope = searchParams.get(JobListFiltersKeys.SCOPE);
  // Job shifts filters
  const openShifts = searchParams.get(JobListFiltersKeys.OPEN_SHIFTS);
  const toApprove = searchParams.get(JobListFiltersKeys.TO_APPROVE);
  const onboarding = searchParams.get(JobListFiltersKeys.ONBOARDING);
  const noCheckInOut = searchParams.get(JobListFiltersKeys.NO_CHECK_IN_OUT);
  const missingHours = searchParams.get(JobListFiltersKeys.MISSING_HOURS);
  const upcoming = searchParams.get(JobListFiltersKeys.UPCOMING);
  const markPaid = searchParams.get(JobListFiltersKeys.MARK_PAID);
  const viewType = searchParams.get(JOB_VIEW_TYPE) as JobListViews;
  const [selectedJob, setSelectedJob] = useState<IJobDetail | undefined>();
  const [jobShareInfo, setJobShareInfo] = useState<JobShareInfo | undefined>();
  const [currentOffset, setCurrentOffset] = useState(0);

  const [newApplicantsCounters, setNewApplicantsCounters] = useState<
    NewApplicantsCounterResults | undefined
  >();
  const queryClient = useQueryClient();
  const { name } = useUserContext();

  const getPeriod = () => {
    return isActive ? (isActive as IJobSearchPeriod) : IJobSearchPeriod.ACTIVE;
  };

  const getScope = () => {
    return scope ? (scope as IJobSearchScope) : IJobSearchScope.MEMBER;
  };

  const getDefaultJobSearchType = () => {
    const periodType = getPeriod();

    const scopeType = getScope();

    switch (periodType) {
      case IJobSearchPeriod.ACTIVE:
        if (scopeType === IJobSearchScope.MEMBER) {
          return IJobSearchType.ACTIVE_MEMBER;
        }
        return IJobSearchType.ACTIVE_COMPANY;
      case IJobSearchPeriod.PAST:
        if (scopeType === IJobSearchScope.MEMBER) {
          return IJobSearchType.PAST_MEMBER;
        }
        return IJobSearchType.PAST_COMPANY;
      default:
        return IJobSearchType.ACTIVE_MEMBER;
    }
  };

  // get initial values from searchParams if they are present, otherwise set initial default values
  const [jobListFilters, setJobListFilters] = useState<IJobListFilters>({
    viewType: viewType !== null ? viewType : JobListViews.JOBS,
    searchFilter: searchFilter || '',
    hiddenJobs: searchParams.get(JobListFiltersKeys.HIDDEN_JOBS) === 'true',
    dateRange: {
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    },
    period: isActive ? (isActive as IJobSearchPeriod) : IJobSearchPeriod.ACTIVE,
    scope: scope ? (scope as IJobSearchScope) : IJobSearchScope.MEMBER,
    newApplicantsOnly:
      searchParams.get(JobListFiltersKeys.NEW_APPLICANTS_ONLY) === 'true',
    jobSearchType: getDefaultJobSearchType(),
    toApprove: toApprove === 'true',
    onboarding: onboarding === 'true',
    noCheckInOut: noCheckInOut === 'true',
    missingHours: missingHours === 'true',
    upcoming: upcoming === 'true',
    markPaid: markPaid === 'true',
    openShifts: openShifts === 'true',
  });

  const {
    isFetchingJobs,
    isFetchingJobShifts,
    isFetchingJobStatsList,
    error,
    jobListResult,
    jobStatsListResult,
    jobShiftsListResult,
    countActiveJobsResult,
    jobShiftsCountersResult,
    jobShiftsStatsListResult,
    newApplicantsCountersResult,
    queryJobList,
    queryJobShiftsList,
    queryJobShiftsCounters,
    refetchJobList,
    refetchJobStatsList,
    refetchJobShifts,
    refetchJobShiftsCounters,
    refetchJobShiftsStatsList,
    refetchCountActiveJobs,
    refetchCountNewApplicants,
  } = useJobListQueries({
    jobListFilters,
    currentOffset,
  });

  const updateSearchParams = () => {
    setSearchParams((previousParams) =>
      updateJobListSearchParams(previousParams, jobListFilters)
    );
  };

  useEffect(() => {
    updateSearchParams();
    refetchJobShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobListFilters.openShifts,
    jobListFilters.onboarding,
    jobListFilters.noCheckInOut,
    jobListFilters.markPaid,
    jobListFilters.missingHours,
    jobListFilters.toApprove,
  ]);

  useEffect(() => {
    setIsLoading(
      isFetchingJobShifts || isFetchingJobs || isFetchingJobStatsList
    );
  }, [isFetchingJobShifts, isFetchingJobStatsList, isFetchingJobs]);

  useEffect(() => {
    setNewApplicantsCounters(newApplicantsCountersResult);
  }, [newApplicantsCountersResult]);

  const mutationMissingActivitiesNoticed = useMissingActivitiesNoticedMutation({
    queryClient,
    queryJobShiftsList,
    queryJobShiftsCounters,
  });

  const mutationMissingHoursNoticed = useMissingHoursNoticedMutation({
    queryClient,
    queryJobShiftsList,
    queryJobShiftsCounters,
  });

  const setViewType = (jobListView: JobListViews) => {
    let { jobSearchType } = jobListFilters;
    if (jobListView === JobListViews.SHIFTS) {
      if (jobListFilters.jobSearchType === IJobSearchType.PAST_MEMBER) {
        jobSearchType = IJobSearchType.ACTIVE_MEMBER;
      } else if (jobListFilters.jobSearchType === IJobSearchType.PAST_COMPANY) {
        jobSearchType = IJobSearchType.ACTIVE_COMPANY;
      }
      setJobListFilters((previousFilters) => ({
        ...previousFilters,
        viewType: jobListView,
        jobSearchType,
        period: IJobSearchPeriod.ACTIVE,
        hiddenJobs: false,
      }));
      searchParams.set(JOB_VIEW_TYPE, jobListView);
      searchParams.set(JobListFiltersKeys.SEARCH_TYPE, jobSearchType);
    } else {
      setJobListFilters((previousFilters) => ({
        ...previousFilters,
        viewType: jobListView,
      }));
      searchParams.set(JOB_VIEW_TYPE, jobListView);
    }
    updateSearchParams();
  };

  useEffect(() => {
    updateSearchParams();

    if (jobListFilters.viewType === JobListViews.JOBS) {
      refetchJobList();
    } else if (jobListFilters.viewType === JobListViews.SHIFTS) {
      refetchJobShifts();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOffset, jobListFilters.newApplicantsOnly]);

  useEffect(() => {
    updateSearchParams();
    if (jobListFilters.viewType === JobListViews.JOBS) {
      refetchJobList();

      if (jobListFilters.period === IJobSearchPeriod.ACTIVE) {
        refetchCountActiveJobs();
        if (jobListFilters.jobSearchType === IJobSearchType.ACTIVE_MEMBER) {
          refetchCountNewApplicants();
        }
      }
    } else if (jobListFilters.viewType === JobListViews.SHIFTS) {
      refetchJobShifts();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobListFilters.jobSearchType, jobListFilters.hiddenJobs]);

  const invalidateJobQuery = () => {
    updateSearchParams();
    setCurrentOffset(0);

    if (jobListFilters.viewType === JobListViews.JOBS) {
      refetchJobList();
      refetchJobStatsList();
      refetchCountActiveJobs();
      refetchCountNewApplicants();
    } else if (jobListFilters.viewType === JobListViews.SHIFTS) {
      refetchJobShifts();
      refetchJobShiftsCounters();
      refetchJobShiftsStatsList();
    }
  };

  const mutationHideJob = useHideJobMutation({
    queryClient,
    jobListFilters,
    jobList: jobListResult,
    jobListQuery: queryJobList,
    setJobListFilters,
  });

  const hideJob = (job: IJobDetail) => {
    mutationHideJob.mutate(job);
  };

  if (error) {
    if (error.response?.status) {
      return <ThError message="No data found" />;
    }
    return <ThError message={error.message} />;
  }

  const showJobMembers = (members: JobMember[]) => {
    setJobMembers(members);
    setShowModalJobMembers(true);
  };

  const closeModalJobMembers = () => {
    setShowModalJobMembers(false);
    setJobMembers([]);
  };

  const handleCloseModalShareJobLink = () => {
    setShowShareJobLinkModal(false);
    setSelectedJob(undefined);
    setJobShareInfo(undefined);
  };

  const handleOpenModalShareJobLink = async (job: IJobDetail) => {
    setIsLoading(true);
    setSelectedJob(job);
    const result = await getJobShareLinkInfo(job.id);
    setJobShareInfo(result);
    setShowShareJobLinkModal(true);
    setIsLoading(false);
  };

  const getShiftsList = (): ActivityDetail[] | undefined =>
    jobShiftsListResult?.jobShifts?.flatMap((item) => item.jobShiftActivities);

  const hasJobList = () => {
    return jobListResult?.results || jobShiftsListResult?.jobs;
  };

  const getJobList = () => {
    if (viewType === JobListViews.JOBS && jobListResult?.results) {
      return jobListResult.results;
    }
    if (viewType === JobListViews.SHIFTS && jobShiftsListResult?.jobs) {
      return jobShiftsListResult.jobs;
    }
    return undefined;
  };

  const completeJobAssignmentOnboarding = (
    jobAssignment: JobAssignment,
    complete: boolean
  ) => {
    const activitiesList = getShiftsList();
    completeJobAssignmentOnboardingUtil({
      jobAssignment,
      complete,
      shiftsList: activitiesList,
      queryClient,
      queryJobShiftsList,
      queryJobShiftsCounters,
    });
  };

  const markAsPaid = async (activity: ActivityDetail) => {
    try {
      setIsLoading(true);

      const successMessage = await markShiftAsPaid(
        activity,
        queryClient,
        queryJobShiftsList,
        queryJobShiftsCounters
      );

      showSuccessAlert(successMessage);
    } catch (err) {
      console.error(`Occurred an error trying to mark a shift as paid`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkPaid = (activity: ActivityDetail) => {
    if (
      activity.shiftPosition.isPaid ||
      (activity.shiftPosition.jobAssignment?.job &&
        isShiftPayable(activity.shiftPosition.jobAssignment.job, activity))
    ) {
      markAsPaid(activity);
    }
  };

  const handleConfirmMarkPaid = (activity: ActivityDetail) => {
    setShowConfirmModal(true);
    getMarkPaidModalConfirmData(
      activity,
      showConfirmModal,
      setModalConfirmModel,
      handleMarkPaid
    );
  };

  const submitHoursSuccess = (activity: ActivityDetail) => {
    onSubmitHoursSuccess(
      activity,
      jobShiftsListResult,
      queryJobShiftsList,
      queryClient
    );
    submitHoursRefreshCounterQueryData(
      queryJobShiftsCounters,
      queryClient,
      activity.shiftPosition.isPaid
    );
  };

  // Called when the user clicks on Mark Paid button from shift review modal
  const markPaidSuccess = (activity: ActivityDetail) => {
    onMarkPaidSuccess(
      activity,
      jobShiftsListResult,
      queryJobShiftsList,
      queryClient
    );
    markPaidRefreshCounterQueryData(
      queryJobShiftsCounters,
      queryClient,
      activity.shiftPosition.isPaid
    );
  };

  // Called when the user clicks on Cancel Shift button from shift review modal
  const cancelShiftSuccess = (activity: ActivityDetail) => {
    onCancelShiftSuccess(
      activity,
      jobShiftsListResult,
      queryJobShiftsList,
      queryClient
    );
  };

  const approveTimeSheetSuccess = (activity: ActivityDetail) => {
    onApproveTimeSheetSuccess(
      activity,
      jobShiftsListResult,
      queryJobShiftsList,
      queryClient,
      name
    );
  };

  const executeApproveTimeSheet = async (activityDetail: ActivityDetail) => {
    approveTimeSheet(
      activityDetail,
      setIsLoading,
      showSuccessAlert,
      showWarningAlert,
      undefined
    );
    approveTimeSheetSuccess(activityDetail);
  };

  const handleMissingActivityNoticedAction = async (
    shiftPositionId: number
  ) => {
    try {
      setIsLoading(true);
      mutationMissingActivitiesNoticed.mutate(shiftPositionId);
    } catch (err) {
      showWarningAlert(
        'An error occurred trying to dismiss the missing activity notification.'
      );
      console.log(
        'An error occurred trying to set missing activities as noticed',
        err
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMissingHourNoticedAction = async (shiftPositionId: number) => {
    try {
      setIsLoading(true);
      mutationMissingHoursNoticed.mutate(shiftPositionId);
    } catch (err) {
      showWarningAlert(
        'An error occurred trying to dismiss the missing hours notification.'
      );
      console.log(
        'An error occurred trying to set missing hours as noticed',
        err
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack>
      {isLoading && <ThLoading />}
      <MobileView>
        <JobListManagerHeaderMobile
          jobListFilters={jobListFilters}
          setCurrentOffset={setCurrentOffset}
          setJobListFilters={setJobListFilters}
          searchJobs={invalidateJobQuery}
          setJobListViewType={setViewType}
          countHiddenJobs={jobListResult?.countHiddenJobs || 0}
        />
      </MobileView>
      <BrowserView>
        {hasJobList() && (
          <JobListManagerHeader
            jobActivities={getShiftsList()}
            setJobListViewType={setViewType}
            jobShiftsCounters={jobShiftsCountersResult}
            countActiveJobs={countActiveJobsResult?.data?.countActiveJobs ?? 0}
            newApplicantsCounters={newApplicantsCounters}
            countHiddenJobs={jobListResult?.countHiddenJobs || 0}
            jobListFilters={jobListFilters}
            jobList={getJobList()}
            setJobListFilters={setJobListFilters}
            setCurrentOffset={setCurrentOffset}
            searchJobs={invalidateJobQuery}
          />
        )}
      </BrowserView>
      {jobListFilters.viewType === JobListViews.JOBS && (
        <JobListView
          jobList={jobListResult}
          jobStatsList={jobStatsListResult}
          isFetchingJobs={isFetchingJobs}
          isFetchingJobStatsList={isFetchingJobStatsList}
          hideJob={hideJob}
          showJobMembers={showJobMembers}
          setCurrentOffset={setCurrentOffset}
          handleOpenModalShareJobLink={handleOpenModalShareJobLink}
        />
      )}
      {jobListFilters.viewType === JobListViews.SHIFTS && (
        <div className="mb-5">
          <JobShiftsView
            jobShiftsResult={jobShiftsListResult}
            jobStatsList={jobShiftsStatsListResult?.results}
            showJobMembers={showJobMembers}
            completeOnboarding={completeJobAssignmentOnboarding}
            handleConfirmMarkPaid={handleConfirmMarkPaid}
            handleOpenModalShareJobLink={handleOpenModalShareJobLink}
            queryJobShiftsList={queryJobShiftsList}
            showAlertMessage={showSuccessAlert}
            setCurrentOffset={setCurrentOffset}
            approveTimeSheet={executeApproveTimeSheet}
            submitHoursSuccess={submitHoursSuccess}
            onMarkPaidSuccess={markPaidSuccess}
            onCancelShiftSuccess={cancelShiftSuccess}
            handleMissingActivityNoticed={handleMissingActivityNoticedAction}
            handleMissingHourNoticed={handleMissingHourNoticedAction}
          />
        </div>
      )}
      {showShareJobLinkModal && selectedJob && jobShareInfo && (
        <div className="ms-auto">
          <ModalShareJobLink
            jobLink={jobShareInfo.jobShareLink}
            jobDescription={jobShareInfo.jobDescription}
            firstRowJob={jobShareInfo.firstRow}
            companyName={selectedJob.company.name}
            companyLogo={selectedJob.company.profileImageUrl}
            jobPosition={selectedJob.position}
            handleClose={handleCloseModalShareJobLink}
          />
        </div>
      )}
      {showConfirmModal && modalConfirmModel && (
        <ModalConfirm
          modalConfirmModel={modalConfirmModel}
          setShowModal={setShowConfirmModal}
        />
      )}
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
      {jobMembers.length > 0 && showModalJobMembers && (
        <ModalJobMembers
          members={jobMembers}
          handleClose={() => closeModalJobMembers()}
        />
      )}
    </Stack>
  );
}
